/**
 * How this console writes money, in one place.
 *
 * A copy of `apps/finance-console/src/money.ts`, with its ledger-specific `sideAbbreviation`
 * dropped because nothing here posts a double entry. A copy rather than a shared package for the
 * reason recorded in `docs/adr/0021-two-consoles-and-no-shared-shell.md`; nothing in it is design
 * content, so the two copies cannot disagree about a design.
 *
 * CLAUDE.md section 2: **"Money is always shown with the currency code and full minor units (for
 * example, KES 1,184,000.00)."** That sentence is this module. One screen in this console writes
 * money — the pricing board's rate-card components — and every amount on it goes through `money()`.
 *
 * Four decisions underneath it, and each is a way the obvious implementation gets it wrong.
 *
 * **The minor unit is read from the currency, not assumed to be two.** `Intl.NumberFormat` knows
 * that KES, USD and EUR have two and that JPY has none, and this platform is multi-tenant across
 * East Africa — UGX and RWF also have none. Dividing by 100 and formatting to two places would
 * write `UGX 41,200.00` for an integer number of shillings, which is a hundredfold error that reads
 * as precision.
 *
 * **Nothing is divided by 100 in floating point.** AGENTS.md: never use floating-point values for
 * money. `baseFareMinor` and its four siblings arrive as JSON numbers — PRC-01's own type, so it is
 * what the console is handed — and the decimal point is placed by splitting the integer's own
 * decimal spelling, never by `value / 100`. `0.07 * 100` is `7.000000000000001`.
 *
 * **The currency code is written out, in front, and is not the symbol.** `Intl`'s own
 * `currencyDisplay: "code"` puts the ISO code where the symbol would go, inserts a non-breaking
 * space, and in some locales puts the code after the number. The design writes `KES 150` with the
 * code first and an ordinary space, so the code is placed here and `Intl` is asked only to group
 * the digits of the major half.
 *
 * **A currency this console cannot place a decimal point in is said rather than guessed.** Both
 * cases are contract violations — PRC-01 validates `currency` as three upper-case letters and
 * publishes every component as an integer — and a board that quietly rounded either would be hiding
 * the thing this module exists to get right.
 */

/**
 * One `Intl.NumberFormat` per currency, cached, and consulted for one thing only.
 *
 * Constructing one is the expensive part. What it is asked is `minimumFractionDigits`, which for a
 * currency-styled formatter is that currency's own minor-unit exponent from the CLDR data — the
 * authority on ISO 4217 that a table written here would be a second, stale opinion about.
 *
 * **What it cannot tell us, measured rather than assumed.** `Intl` throws `RangeError` only on a
 * code that is not three ASCII letters: `ZZ`, `zzzz`, `K1S` and `""` all throw, and `ZZZ` — a
 * well-formed code ISO 4217 does not assign — comes back with CLDR's default of two decimal places,
 * exactly as `KES` does. So there is no way through this API to tell "two decimals because ISO says
 * so" from "two decimals because nothing said". That is accepted rather than worked around: the
 * contract validates the code, and a well-formed unassigned code is a row the platform wrote wrong
 * rather than a formatting decision this module can rescue.
 */
const formatters = new Map<string, Intl.NumberFormat | undefined>();

function formatterFor(currency: string): Intl.NumberFormat | undefined {
  if (formatters.has(currency)) return formatters.get(currency);
  let made: Intl.NumberFormat | undefined;
  try {
    made = new Intl.NumberFormat("en-GB", { style: "currency", currency });
  } catch {
    // A malformed code throws `RangeError`. Cached as absent so the throw happens once per code
    // rather than once per row, and handled in `money()` rather than papered over.
    made = undefined;
  }
  formatters.set(currency, made);
  return made;
}

/**
 * How many minor units make one major unit of this currency, as a count of decimal places.
 *
 * `undefined` only when the code is not three ASCII letters — see `formatterFor` for why that is
 * the whole of what can be detected here.
 */
export function minorUnitDigits(currency: string): number | undefined {
  return formatterFor(currency)?.resolvedOptions().minimumFractionDigits;
}

/** Digit grouping for the major half, with no currency and no fraction of its own. */
const grouping = new Intl.NumberFormat("en-GB", { useGrouping: true, maximumFractionDigits: 0 });

/**
 * One amount, written the way every rule on this platform requires: the code, then the full minor
 * units.
 *
 * @param amountMinor the integer the contract publishes. PRC-01's `components.baseFareMinor` and
 *   its four siblings are JSON integers in the currency's minor unit.
 * @param currency the ISO 4217 code the same response publishes beside it. Never defaulted: a
 *   console that assumed KES would write the wrong code on a Ugandan tenant's board and the digits
 *   beside it would be right, which is the worst shape a money bug can have.
 */
export function money(amountMinor: number, currency: string): string {
  const digits = minorUnitDigits(currency);
  if (digits === undefined || !Number.isInteger(amountMinor)) {
    return `${currency} ${String(amountMinor)} minor units`;
  }

  /*
   * The decimal point, placed by splitting a string rather than by dividing a number.
   *
   * `amountMinor` is an integer, so its decimal spelling is exact. Splitting that spelling at the
   * currency's own minor-unit boundary gives the two halves with no arithmetic on the amount at
   * all, where `amountMinor / 100` is a binary float that is only *nearly* the right decimal.
   *
   * `padStart(digits + 1, "0")` is what makes 7 minor units read `0.07` rather than `.7`: the pad
   * guarantees at least one digit before the split.
   */
  const unsigned = String(Math.abs(amountMinor)).padStart(digits + 1, "0");
  const major = unsigned.slice(0, unsigned.length - digits);
  const fraction = digits === 0 ? "" : `.${unsigned.slice(unsigned.length - digits)}`;

  return `${currency} ${amountMinor < 0 ? "−" : ""}${grouping.format(Number(major))}${fraction}`;
}
